import { Spinner } from "@/components/ui/spinner";
import { useState } from "react";
import { Pencil, PowerOff } from "lucide-react";
import { dayjs } from "@boo/shared";
import type { RecurringExpenseItem } from "@boo/shared";
import { useRecurringExpenseStore } from "../store/useRecurringExpenseStore";

const FREQ_LABEL: Record<string, string> = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
};

const CATEGORY_LABEL: Record<string, string> = {
  FOOD: "Food",
  TRANSPORT: "Transport",
  HOUSING: "Housing",
  ENTERTAINMENT: "Entertainment",
  HEALTH: "Health",
  FINANCIAL: "Financial",
  INVESTMENT: "Investment",
  SHOPPING: "Shopping",
  BILLS: "Bills",
  OTHER: "Other",
};

interface Props {
  item: RecurringExpenseItem;
  onEdit?: (item: RecurringExpenseItem) => void;
}

export default function RecurringExpenseRow({ item, onEdit }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const deactivateExpense = useRecurringExpenseStore(
    (s) => s.deactivateExpense,
  );

  async function handleDeactivate() {
    setDeactivating(true);
    try {
      await deactivateExpense(item.id);
    } finally {
      setDeactivating(false);
      setConfirming(false);
    }
  }

  return (
    <div className="rounded-[10px] bg-[var(--surface-muted)] border-[0.5px] border-border/50">
      <div className="grid items-center gap-3 px-4 py-3 grid-cols-[1fr_auto]">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-[13px] font-medium truncate ${item.isActive ? "text-foreground" : "text-muted-foreground"}`}
            >
              {item.title}
            </span>
            {!item.isActive && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                Inactive
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {CATEGORY_LABEL[item.category] ?? item.category} ·{" "}
            {FREQ_LABEL[item.frequency] ?? item.frequency}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Next due{" "}
            <span className="text-foreground">
              {dayjs(item.nextDueDate).format("D MMM YYYY")}
            </span>
            {" · "}
            Last paid{" "}
            <span className="text-foreground">
              {item.lastPaidDate
                ? dayjs(item.lastPaidDate).format("D MMM YYYY")
                : "—"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[14px] font-bold text-foreground [font-variant-numeric:tabular-nums]">
            ฿{item.amount.toLocaleString("th-TH")}
          </span>
          {item.isActive && !confirming && (
            <button
              onClick={() => setConfirming(true)}
              className="p-2 sm:p-1.5 rounded-[6px] text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/20 transition-colors cursor-pointer"
              aria-label={`Deactivate ${item.title}`}
            >
              <PowerOff className="w-3.5 h-3.5" />
            </button>
          )}
          {onEdit && !confirming && (
            <button
              onClick={() => onEdit(item)}
              className="p-2 sm:p-1.5 rounded-[6px] text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] border border-transparent hover:border-border transition-colors cursor-pointer"
              aria-label={`Edit ${item.title}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <div className="px-4 pb-3">
          <div className="rounded-[8px] border border-destructive/25 bg-destructive/5 px-3 py-2.5 flex flex-col gap-2">
            <div>
              <p className="text-[12px] font-semibold text-foreground">
                Deactivate "{item.title}"?
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                It will be removed from your active list and totals. All past
                payment history stays intact.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDeactivate}
                disabled={deactivating}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-[6px] bg-destructive text-white text-[11px] font-medium hover:opacity-80 disabled:opacity-50 transition-opacity cursor-pointer"
              >
                {deactivating && <Spinner className="size-3.5" />}
                {deactivating ? "Deactivating…" : "Deactivate"}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={deactivating}
                className="px-3 py-1 rounded-[6px] border border-border text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer"
              >
                Keep active
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
